/**
 * Guest cart expiration.
 *
 * A logged-out visitor's cart lives entirely in `localStorage` (`GERMANOS_CART_V1`),
 * so without an explicit rule it survives forever — a visitor could return a month
 * later and still find the cart exactly as they left it. This module discards such a
 * cart once it has been inactive for longer than the admin-configured window.
 *
 * <p><b>Inactivity</b> is measured from the `updatedAt` claim of the cart JWT, which
 * grm-ordering only refreshes when the cart is actually mutated (add / remove /
 * quantity / shipping) — plain browsing never resets it.
 *
 * <p><b>Timing.</b> {@link purgeExpiredGuestCart} is called from `onClientEntry` in
 * `gatsby-browser.js`, before the app bundle evaluates `hooks/useCart.ts` (whose
 * `hydrate()` IIFE reads `localStorage` synchronously at module scope). Purging any
 * later would let the stale items paint in the header badge and mini-cart for a frame
 * before disappearing.
 *
 * <p><b>Scope.</b> Logged-in customers are never touched: their cart is server-side and
 * is meant to persist across sessions.
 */

/** localStorage key holding the storefront cart (items, cart JWT, checkout session, delivery prefs). */
const CART_STORAGE_KEY = 'GERMANOS_CART_V1';

/** Pre-consolidation cart token key; cleared alongside the cart so no stale token survives. */
const LEGACY_CART_TOKEN_KEY = 'GERMANOS_CART_TOKEN';

/** sessionStorage key holding the cached checkout response derived from the cart. */
const CHECKOUT_CACHE_KEY = 'GERMANOS_CHECKOUT_CACHE';

/** Non-sensitive customer auth flag written by AuthContext after OAuth login. */
const CUSTOMER_AUTH_INFO_KEY = 'customerAuthInfo';

/** Admin/dev bearer token (non-Cosmote-ID auth). */
const DEV_AUTH_TOKEN_KEY = 'userAuthToken';

/** Last value fetched from the admin setting, reused on the next boot for a synchronous decision. */
const TTL_CACHE_KEY = 'GERMANOS_CART_TTL_MINUTES';

/** Mirrors `CartAbandonmentSettings.DEFAULT_GUEST_CART_EXPIRATION_MINUTES` on grm-store-front. */
export const DEFAULT_GUEST_CART_EXPIRATION_MINUTES = 1440;

const MINUTE_IN_MS = 60_000;

/**
 * Resolve the BFF origin the same way `lib/cart-api.ts` does, so the settings call
 * goes to grm-bff rather than the Gatsby dev server.
 */
function getApiBaseUrl(): string {
  const injected = (window as unknown as { __API_BASE__?: string }).__API_BASE__;
  if (injected && injected !== '__GATSBY_CLIENT_TARGET__') {
    return injected;
  }
  if (typeof process !== 'undefined' && process.env?.GATSBY_CLIENT_TARGET) {
    return process.env.GATSBY_CLIENT_TARGET;
  }
  return 'http://localhost:4002';
}

/**
 * Whether the visitor is signed in through any of the storefront auth systems.
 *
 * <p>Deliberately synchronous and localStorage-only: the authoritative signal is the
 * HttpOnly `customer_access_token` cookie, but it cannot be read from JS and resolving
 * it would require awaiting `/api/auth/cosmote-id/me`, which would delay the first
 * paint for every visitor. The cached flag is written by AuthContext on login and
 * cleared on logout / session expiry, so it tracks the cookie closely enough for a
 * decision whose only failure mode is keeping a cart slightly longer than configured.
 */
function isSignedIn(): boolean {
  try {
    if (localStorage.getItem(DEV_AUTH_TOKEN_KEY)) return true;
    const info = localStorage.getItem(CUSTOMER_AUTH_INFO_KEY);
    return info ? JSON.parse(info).authenticated === true : false;
  } catch {
    return false;
  }
}

/**
 * Read the `updatedAt` claim (epoch millis) from the cart JWT held in storage.
 *
 * @returns the timestamp of the last cart mutation, or null when there is no cart,
 *          no token, or the token cannot be decoded
 */
function readCartUpdatedAt(): number | null {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return null;

    const token = (JSON.parse(raw) as { cartToken?: string | null }).cartToken;
    if (!token) return null;

    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // base64url → base64, then percent-decode so non-ASCII claims (e.g. Greek
    // display names on cart lines) survive `atob`'s byte-oriented output.
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    const payload = JSON.parse(json) as { updatedAt?: number };
    return typeof payload.updatedAt === 'number' ? payload.updatedAt : null;
  } catch {
    return null;
  }
}

/**
 * Read the last known expiration window.
 *
 * @returns the cached admin value, or {@link DEFAULT_GUEST_CART_EXPIRATION_MINUTES}
 *          when nothing has been cached yet
 */
function readCachedTtlMinutes(): number {
  try {
    const raw = localStorage.getItem(TTL_CACHE_KEY);
    if (raw === null) return DEFAULT_GUEST_CART_EXPIRATION_MINUTES;
    const cached = Number(raw);
    return Number.isFinite(cached) && cached >= 0 ? cached : DEFAULT_GUEST_CART_EXPIRATION_MINUTES;
  } catch {
    return DEFAULT_GUEST_CART_EXPIRATION_MINUTES;
  }
}

/**
 * Fetch the current expiration window from the admin setting and cache it for the
 * next boot. Fire-and-forget: the value is never awaited, so it costs nothing on the
 * critical path. A change made in the admin therefore takes effect from the visitor's
 * next page load onwards.
 */
function refreshTtlMinutes(): void {
  fetch(`${getApiBaseUrl()}/api/cms/other-settings/cart-abandonment`)
    .then((response) => (response.ok ? response.json() : null))
    .then((data: { guestCartExpirationMinutes?: number } | null) => {
      const minutes = data?.guestCartExpirationMinutes;
      if (typeof minutes === 'number' && minutes >= 0) {
        localStorage.setItem(TTL_CACHE_KEY, String(minutes));
      }
    })
    .catch(() => {
      // Offline or BFF unreachable — keep the cached value.
    });
}

/** Remove the cart and everything derived from it (checkout session, delivery choices). */
function clearGuestCart(): void {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
    localStorage.removeItem(LEGACY_CART_TOKEN_KEY);
    sessionStorage.removeItem(CHECKOUT_CACHE_KEY);
  } catch {
    // Storage unavailable (private mode / quota) — nothing to clean up.
  }
}

/**
 * Discard the guest cart when it has been inactive longer than the configured window.
 *
 * <p>Must run before `hooks/useCart.ts` is evaluated, otherwise the expired items are
 * already in the in-memory store and would flash in the UI.
 */
export function purgeExpiredGuestCart(): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith('/backoffice')) return;
  if (isSignedIn()) return;

  const updatedAt = readCartUpdatedAt();
  if (updatedAt === null) return;

  const ttlMinutes = readCachedTtlMinutes();
  if (ttlMinutes > 0 && Date.now() - updatedAt > ttlMinutes * MINUTE_IN_MS) {
    clearGuestCart();
  }

  refreshTtlMinutes();
}
